/**
 * The **Cinema Day** — a cinema's business day, running 04:00 until 03:59:59.999 the next morning,
 * so a late screening belongs to the day it was programmed under rather than the calendar day it
 * ends in. See CONTEXT.md → "Programme & scheduling".
 *
 * This is the only definition of that boundary. It used to exist six times: once here (correct) and
 * once in each of admin, distributor, cinema, cinema-website and b2b-marketing, all five of which
 * ended the day at 03:59:00.000 and so dropped the last 59.999 seconds — a nightly window in which a
 * showing belonged to no Cinema Day at all (CIN-14663).
 */

// `TimeZone` is a bare type alias, so it is imported as a type: `locale-helper` reaches react and
// @cineamo/api at runtime, and the "[frontend] Tests" step runs with no node_modules. The
// type-stripper erases this line; a value import would abort test/cinema-day.test.mjs in CI.
import type { TimeZone } from './locale-helper';
import { cinemaWallClockToInstant, instantToCinemaWallClock } from './time-zone.ts';

/** The hour a Cinema Day begins. Everything below derives its boundary from this. */
export const CINEMA_DAY_START_HOUR = 4;

/**
 * The instant the Cinema Day containing `date` begins, measured on the cinema's own clock.
 *
 * The plain {@link cinemaDayStart} reads 04:00 in the *browser's* zone, which puts the boundary in
 * the wrong place for anyone not sitting in the cinema's: from the UK the Sept 9 window ran to
 * 02:59Z instead of 01:59Z, so a Berlin screening at 04:47 on the 10th — Cinema Day the 10th — was
 * fetched into the 9th and drawn near the top of the wrong day.
 */
export function cinemaDayStartInZone(date: Date | string, timeZone: TimeZone): Date {
    const wallClock = instantToCinemaWallClock(date, timeZone);
    wallClock.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);
    return cinemaWallClockToInstant(wallClock, timeZone);
}

/**
 * The calendar date (`YYYY-MM-DD`) of the Cinema Day `instant` belongs to, on the cinema's clock —
 * a stable key for grouping showings under day headings.
 *
 * Grouping on the raw calendar date instead files a 03:47 screening under the following morning,
 * which contradicts a date filter built from {@link cinemaDayStartInZone} on the same page: the
 * filter includes the showing in one day while the heading above it names the next.
 */
export function cinemaDayKeyInZone(instant: Date | string, timeZone: TimeZone): string {
    const wallClock = instantToCinemaWallClock(instant, timeZone);
    if (wallClock.getHours() < CINEMA_DAY_START_HOUR) {
        wallClock.setDate(wallClock.getDate() - 1);
    }
    const month = `${wallClock.getMonth() + 1}`.padStart(2, '0');
    const day = `${wallClock.getDate()}`.padStart(2, '0');
    return `${wallClock.getFullYear()}-${month}-${day}`;
}

/** The last instant of the Cinema Day that began on `date`, on the cinema's clock. */
export function cinemaDayEndInZone(date: Date | string, timeZone: TimeZone): Date {
    const wallClock = instantToCinemaWallClock(date, timeZone);
    wallClock.setDate(wallClock.getDate() + 1);
    wallClock.setHours(CINEMA_DAY_START_HOUR - 1, 59, 59, 999);
    return cinemaWallClockToInstant(wallClock, timeZone);
}

/** 04:00 on the calendar day of `date`. */
export function cinemaDayStart(date: Date | string): Date {
    const start = new Date(date);
    start.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);
    return start;
}

/** The last instant of the Cinema Day that began on `date` — 03:59:59.999 the following morning. */
export function cinemaDayEnd(date: Date | string): Date {
    const end = new Date(date);
    end.setHours(CINEMA_DAY_START_HOUR - 1, 59, 59, 999);
    end.setDate(end.getDate() + 1);
    return end;
}

/**
 * The Cinema Day `instant` actually falls inside, which before 04:00 is still the previous calendar
 * day's.
 *
 * Anchoring on a raw `new Date()` at 02:00 would produce a window running 04:00 today to 03:59
 * tomorrow — entirely in the future, so anything counting over it reports zero for the four hours
 * after midnight.
 */
export function cinemaDayOf(instant: Date | string): Date {
    const anchor = new Date(instant);
    if (anchor.getHours() < CINEMA_DAY_START_HOUR) {
        anchor.setDate(anchor.getDate() - 1);
    }
    anchor.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);
    return anchor;
}
